import React, { useState, useRef, useEffect } from 'react'
import classNames from 'classnames'
import Image from '@toasttab/shared/src/components/atoms/Image/Image'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import { ContentfulImageAPI } from '@toasttab/shared/src/utils/contentfulUtils'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as LinkedIn } from '@toasttab/shared/src/assets/svg/social/linkedin.svg'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as ToastShape } from '@toasttab/shared/src/assets/svg/logo-toast-shape.svg'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as Cancel } from '@toasttab/shared/src/assets/svg/circle-ui-actions/cancel.svg'

export interface Bio {
  name: string
  headshot: any
  jobTitle: string
  favoriteToast?: string
  bio: any
  type: string
  contentful_id: string
  linkedIn?: string
  __typename: string
  bioNum?: number
}

const BioCard = ({
  name,
  headshot,
  jobTitle,
  favoriteToast,
  linkedIn,
  bio,
  type,
  bioNum = 1
}: Bio) => {
  const [showModal, setShowModal] = useState(false)
  const modalRef = useRef(null)

  let query = ContentfulImageAPI.transformImageAvif({
    width: 259,
    height: 259,
    resize: 'fill',
    focusArea: 'top',
    extension: headshot.file?.contentType
  })
  let headshotQuery = ContentfulImageAPI.transformImageAvif({
    width: 100,
    height: 100,
    resize: 'fill',
    extension: headshot.file?.contentType
  })
  let optimizedImages = ContentfulImageAPI.transformImageAvifSrcset({
    url: headshot.file.url,
    widths: [214, 259, 400],
    heights: [214, 259, 400],
    resize: 'fill',
    focusArea: 'top',
    extension: headshot.file?.contentType
  })

  let modalClasses = classNames({
    'fixed top-0 left-0 top-0 right-0 overflow-hidden w-full h-full bg-black bg-opacity-75  text-center p-5 flex flex-col justify-center items-center close-trigger':
      true,
    'opacity-0 z-[-1]': !showModal,
    'opacity-100 z-[1000]': showModal
  })

  useEffect(() => {
    document.getElementsByTagName('body')[0].style.overflowY = showModal
      ? 'hidden'
      : 'scroll'
  }, [showModal])

  return (
    <>
      <div
        className='flex flex-col h-full rounded-lg bg-white border border-light cursor-pointer hover:scale-105 transition-transform duration-300 ease-out '
        data-testid={`BioCard-${bioNum}`}
      >
        <button
          className='!text-default hover:!text-default h-'
          onClick={() => setShowModal(!showModal)}
          aria-haspopup='dialog'
        >
          <div className='w-full relative'>
            <Image
              url={headshot.file.url + query}
              imageAlt={headshot.title}
              optimizedImages={{
                srcsetAvif: optimizedImages,
                srcset: optimizedImages
              }}
              classes='rounded-t-lg'
            />
          </div>
          <div className='pt-4 px-5 pb-[25px] text-center'>
            <Typography variation='h4' as='h4' classes='mb-2'>
              {name}
            </Typography>
            <Typography variation='default' as='p' classes='text-secondary'>
              {jobTitle}
            </Typography>
          </div>
        </button>
      </div>
      <div
        className={modalClasses}
        onClick={(e) => {
          const targetClass = (e.target as HTMLElement).classList
          if (targetClass.contains('close-trigger')) {
            setShowModal(false)
          }
        }}
        role='dialog'
        aria-modal='true'
        data-testid={`BioCardModal-${bioNum}`}
      >
        <div
          className='max-w-[500px] relative text-left rounded-md bg-white shadow-[0px_0px_10px_#000] p-4 md:p-10 z-[1001] md:max-h-[80vh] md:h-auto flex flex-col'
          ref={modalRef}
          role='document'
          aria-labelledby='bioCard-title'
          aria-describedby='bioCard-bio'
        >
          <button
            onClick={() => setShowModal(false)}
            className='absolute -top-3 -right-3 cursor-pointer'
            tabIndex={0}
          >
            {/* Get an actual SVG here that can have a bg color of white */}
            <div className='w-3 h-3 bg-white absolute top-[25%] left-[25%] rounded-round -z-[1]' />
            <Cancel className='fill-gray-125 z-20' />
          </button>
          <div className='flex items-center mb-[10px] mb-4 gap-[20px]'>
            <div className='bio-image-container relative w-25 mb-[10px] flex-shrink-0 flex-grow-0'>
              <Image
                url={headshot.file.url + headshotQuery}
                imageAlt={headshot.title}
                classes='rounded-round'
              />
              {linkedIn && (
                <a
                  href={linkedIn}
                  target='_blank'
                  className='absolute bottom-0 right-0'
                  rel='noopener noreferrer'
                >
                  <LinkedIn className='fill-brand-50' />
                </a>
              )}
            </div>
            <div className='pl-[20px]'>
              <Typography variation='h4' as='h4' id='bioCard-title'>
                {name}
              </Typography>
              <Typography variation='default' as='p' classes='text-secondary'>
                {jobTitle}
              </Typography>
            </div>
          </div>
          {bio?.bio && (
            <Typography
              variation='default'
              as='p'
              classes='pb-[30px] border-b border-light text-secondary max-h-64 overflow-y-scroll md:max-h-[80%]'
              id='bioCard-bio'
            >
              {bio?.bio}
            </Typography>
          )}
          {favoriteToast && (
            <div className='pt-10 inline-flex'>
              <ToastShape className='w-[25px] h-[25px] mr-4 ' />
              <Typography
                variation='default'
                as='p'
                classes='text-secondary'
                allowHtml={false}
              >
                <span className='font-bold'>Favorite Toast</span>
                <span className='inline-block pl-5'>{favoriteToast}</span>
              </Typography>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default BioCard
